import React from "react";
import { Button } from "@/components/Base/Button";
import { Price } from "@/types/v2/Stripe";
import { cn } from "@/lib/utils";
import { Icon } from "@/components/Common/Icon";
import { formatAmount, formatInterval } from "@/lib/stripe";

export interface StripeSubscriptionProps {
  price: Price;
  onSelect: (price: Price) => void;
  selected?: boolean;
  className?: string;
}

export const StripeSubscription = React.forwardRef<
  HTMLDivElement,
  StripeSubscriptionProps
>(({ price, onSelect, selected = false, className }, ref) => {
  const handleSelect = () => {
    onSelect(price);
  };

  return (
    <div
      ref={ref}
      className={cn(
        "flex flex-col rounded-lg border p-6 transition-all",
        selected
          ? "border-primary bg-primary/75 shadow-md"
          : "border-border bg-primary/50 hover:border-primary/50 hover:shadow-sm",
        className
      )}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-medium">
              {price.metadata.name || "Subscription"}
            </h3>
            {price.metadata.description && (
              <p className="mt-1 text-sm text-primary-foreground">
                {price.metadata.description}
              </p>
            )}
          </div>
          {selected && (
            <div className="rounded bg-accent px-2 py-1 text-xs font-medium text-accent-foreground">
              Selected
            </div>
          )}
        </div>

        <div className="flex items-baseline">
          <span className="text-3xl font-bold">
            {formatAmount(price.unit_amount, price.currency)}
          </span>
          {price.recurring && (
            <span className="ml-1 text-sm text-primary-foreground">
              {formatInterval(price.recurring)}
            </span>
          )}
        </div>

        {price.metadata.features && (
          <ul className="mt-2 space-y-2">
            {price.metadata.features.split(",").map((feature, index) => (
              <li key={index} className="flex items-start gap-2 align-middle">
                <Icon
                  name="circleCheck"
                  className={"fill-black stroke-white"}
                />
                <span className="text-sm">{feature.trim()}</span>
              </li>
            ))}
          </ul>
        )}

        <Button
          onClick={handleSelect}
          variant={selected ? "accent" : "outline"}
          className="mt-4"
          fullWidth
        >
          {selected ? "Selected" : "Select"}
        </Button>
      </div>
    </div>
  );
});

StripeSubscription.displayName = "StripeSubscription";
